import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { tourStops } from 'fym-shared/src/music24/onboardingTour';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { useTourActive, useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { setTourActive } from '../../../redux/slices/appInfo/slice';
import { useArtistSwitcher } from '../../accountSelector/useArtistSwitcher';
import { useCurrentNavScreen } from '../../music24/navigation/hooks/useCurrentNavScreen';
import { findArtistItemParent } from '../../music24/navigation/smlNavigation';
import { DEMO_ACCOUNT_ID } from '../../accountSelector/demoAccounts';
import { useOnboardingState } from '../useOnboardingState';
import { onboardingIntentOf } from '../signupIntent';
import { tourRoute } from './tourRoutes';
import { useDemoSongUuid } from './useDemoSongUuid';
import { storeTourActive } from './tourSession';
import { markTourResumeChecked, wasTourResumeChecked } from './tourResume';

interface UseTourResumeOptions {
  /** `false` in public mode, in the native web view, or when nobody is signed in. */
  enabled: boolean;
}

/**
 * Goes on with a tour that the person started on another device or in another tab (Ruling F4).
 * The server says `tour.active` (started and not finished). The first answer of a page load
 * decides: the hook selects the demo artist, turns the guide bar on and opens the stored stop.
 * A tab that already runs the tour (the session flag) changes nothing here.
 *
 * A page load on one item of an artist (a release page from a share link or an email, one
 * smartlink) does not resume: the person opened that item on purpose. The demo artist switch
 * would send them to the parent list (`findArtistItemParent`), so a member that opened "Open in
 * release" landed on the bare Releases list (incident 2026-10-09). The tour goes on at the next
 * page load.
 */
export function useTourResume({ enabled }: UseTourResumeOptions): void {
  const dispatch = useDispatch();
  const { push } = useSMLRouter();
  const isTourActive = useTourActive();
  const userId = useUserTokenDecoded()?.id ?? null;
  const { state } = useOnboardingState();
  const { onSelect } = useArtistSwitcher();
  const { screen } = useCurrentNavScreen();
  const isItemPage = findArtistItemParent(screen) !== undefined;
  // The stop to open after the resume; null when no resume waits for its page.
  const [resumeStep, setResumeStep] = useState<number | null>(null);
  const demoSongUuid = useDemoSongUuid(resumeStep !== null);

  useEffect(() => {
    if (!enabled || userId === null || state === undefined || wasTourResumeChecked(userId)) {
      return;
    }
    markTourResumeChecked(userId);
    if (isItemPage || isTourActive || state.tour.finished || state.tour.active !== true || state.showWelcome) {
      return;
    }
    // The tour is the one path that selects the demo artist.
    onSelect(DEMO_ACCOUNT_ID);
    dispatch(setTourActive({ active: true }));
    storeTourActive(true);
    setResumeStep(state.tour.step);
  }, [dispatch, enabled, isItemPage, isTourActive, onSelect, state, userId]);

  const intent = onboardingIntentOf(state);
  useEffect(() => {
    if (resumeStep === null) {
      return;
    }
    const stops = tourStops(intent);
    const stop = stops[Math.min(Math.max(resumeStep, 0), stops.length - 1)];
    if (stop === undefined) {
      setResumeStep(null);
      return;
    }
    // The kit and plan stops need the demo song. The page opens when the demo catalog answers.
    if ((stop === 'kit' || stop === 'plan') && demoSongUuid === '') {
      return;
    }
    setResumeStep(null);
    const { route, params } = tourRoute(stop, demoSongUuid);
    push(route, params);
  }, [demoSongUuid, intent, push, resumeStep]);
}
